/**
 * IndexedDB 유틸리티 생성
 * @param options - 옵션
 * @param options.dbName - 데이터베이스 이름 (어플리케이션명 ex: Template)
 * @param options.storeName - 스토어 이름 (버전 형식 ex: v1)
 * @param options.version - DB 버전 (메이저 버전과 동일하게 관리)
 */
export const createIndexedDB = (options: {
  dbName: string;
  storeName: string;
  version: number;
}) => {
  let connection: {
    database: Promise<IDBDatabase> | null;
    status: 'connecting' | 'connected' | 'disconnected';
  } = {
    database: null,
    status: 'disconnected',
  };

  const isClient = typeof window !== 'undefined';

  const connect = (): Promise<IDBDatabase> => {
    if (!isClient)
      return Promise.reject(
        new Error('IndexedDB is not available on server side'),
      );

    if (connection.status === 'connecting' && connection.database)
      return connection.database;
    if (connection.status === 'connected' && connection.database)
      return connection.database;

    connection.status = 'connecting';

    connection.database = new Promise((resolve, reject) => {
      try {
        const request = window.indexedDB.open(options.dbName, options.version);

        request.onerror = () => {
          connection = { database: null, status: 'disconnected' };

          reject(new Error('Failed to connect to DB'));
        };

        request.onblocked = () => {
          connection = { database: null, status: 'disconnected' };

          reject(new Error('Database blocked'));
        };

        request.onupgradeneeded = (event) => {
          const database = (event.target as IDBOpenDBRequest).result;

          for (const name of database.objectStoreNames) {
            database.deleteObjectStore(name);
          }

          database.createObjectStore(options.storeName);
        };

        request.onsuccess = () => {
          const database = request.result;

          database.onclose = () => {
            connection = { database: null, status: 'disconnected' };
          };

          database.onversionchange = () => {
            database.close();

            connection = { database: null, status: 'disconnected' };
          };

          connection.status = 'connected';

          resolve(database);
        };
      } catch (error) {
        connection = { database: null, status: 'disconnected' };

        reject(error);
      }
    });

    return connection.database;
  };

  /**
   * 데이터 로드
   * @param key - 데이터 키
   */
  const load = async <T>(key: string): Promise<T | null> => {
    if (!isClient) return null;

    if (!key) throw new Error('Invalid key');

    try {
      const database = await connect();

      return new Promise((resolve, reject) => {
        const transaction = database.transaction(options.storeName, 'readonly');
        const store = transaction.objectStore(options.storeName);
        const request = store.get(key);

        request.onsuccess = () => resolve(request.result ?? null);
        request.onerror = () => reject(new Error('Failed to load data'));
      });
    } catch (error) {
      console.error('Load error:', error);

      return null;
    }
  };

  /**
   * 데이터 저장
   * @param key - 데이터 키
   * @param value - 데이터 값
   */
  const save = async <T>(key: string, value: T): Promise<void> => {
    if (!isClient) return;

    if (!key) throw new Error('Invalid key');

    try {
      const database = await connect();

      return new Promise((resolve, reject) => {
        const transaction = database.transaction(
          options.storeName,
          'readwrite',
        );
        const store = transaction.objectStore(options.storeName);

        store.put(value, key);

        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(new Error('Failed to save data'));
      });
    } catch (error) {
      console.error('Save error:', error);
    }
  };

  /**
   * 데이터 삭제
   * @param key - 데이터 키
   */
  const remove = async (key: string): Promise<void> => {
    if (!isClient) return;

    if (!key) throw new Error('Invalid key');

    try {
      const database = await connect();

      return new Promise((resolve, reject) => {
        const transaction = database.transaction(
          options.storeName,
          'readwrite',
        );
        const store = transaction.objectStore(options.storeName);
        const request = store.delete(key);

        request.onsuccess = () => resolve();
        request.onerror = () => reject(new Error('Failed to remove data'));
      });
    } catch (error) {
      console.error('Remove error:', error);
    }
  };

  return {
    load,
    save,
    remove,
  };
};
